import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import LiffContext from '../context/LiffContext';
import LiffService from '../../../services/LiffService';
import readQrData from '../../../utils/readQrData';
import { fmtBaht as fmt } from '../utils/formatters';

const POLL_INTERVAL_MS = 5000;

/** Trigger download of QR image (data URL). On mobile, may open image so user can long-press to save. */
const downloadDataUrl = (dataUrl, filename = 'qr-promptpay-charge.png') => {
  if (!dataUrl || typeof dataUrl !== 'string') return;
  try {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    link.click();
  } catch (_e) {}
};

/**
 * Superflow payment page: Group1 and Group2 on the same continuous page.
 * Both payment methods (sections) visible at once; after paying one, the other stays visible.
 * Handles return from external credit card (Beam): refetches summary and shows remaining group.
 */
const LiffPaymentPage = ({ items = [], paymentStatus, onPaymentComplete, onBack, onViewOrderSummary }) => {
  const { accessToken, shopId } = React.useContext(LiffContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // Group summary fetched from backend
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');

  // Group1 state
  const [g1Method, setG1Method] = useState('');
  const [g1Loading, setG1Loading] = useState(false);
  const [g1Error, setG1Error] = useState('');
  const [g1QrImage, setG1QrImage] = useState(null);
  const [g1Paid, setG1Paid] = useState(false);

  // Group2 state
  const [g2Method, setG2Method] = useState('');
  const [g2Loading, setG2Loading] = useState(false);
  const [g2Error, setG2Error] = useState('');
  const [g2BankInfo, setG2BankInfo] = useState(null);
  const [g2Paid, setG2Paid] = useState(false);
  const [g2ShowBankInfo, setG2ShowBankInfo] = useState(false);

  const pollRef = useRef(null);
  const hadBothGroupsRef = useRef(false);
  const group1SectionRef = useRef(null);
  const group2SectionRef = useRef(null);

  const hasGroup1 = summary?.group1Total > 0;
  const hasGroup2 = summary?.group2Total > 0;
  const hasBothGroups = hasGroup1 && hasGroup2;
  const singlePaymentOnly = hasGroup2 && !hasGroup1; // Non-company: one block (bank transfer + credit card)
  const afterGroup1 = searchParams.get('after') === 'group1';
  const afterGroup2 = searchParams.get('after') === 'group2';

  // Remember we had both groups (so after one paid we still show "ชำระแล้ว" for that group)
  if (hasBothGroups) hadBothGroupsRef.current = true;
  const showGroup1Done = (hadBothGroupsRef.current && hasGroup2 && !hasGroup1) || afterGroup1;
  const showGroup2Done = (hadBothGroupsRef.current && hasGroup1 && !hasGroup2) || afterGroup2;

  const fetchSummary = React.useCallback(async () => {
    if (!accessToken || !shopId) return null;
    try {
      const data = await LiffService.getPaymentSummary(accessToken, shopId);
      setSummary(data);
      if (data.bankInfo) setG2BankInfo(data.bankInfo);
      return data;
    } catch (err) {
      setSummaryError(err.response?.data?.message || err.message || 'ไม่สามารถโหลดข้อมูลชำระเงินได้');
      return null;
    }
  }, [accessToken, shopId]);

  // Fetch summary on mount and when returning from external (e.g. credit card)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accessToken || !shopId) return;
      setSummaryLoading(true);
      setSummaryError('');
      try {
        const data = await LiffService.getPaymentSummary(accessToken, shopId);
        if (cancelled) return;
        setSummary(data);
        setG2BankInfo(data.bankInfo || null);
      } catch (err) {
        if (!cancelled) setSummaryError(err.response?.data?.message || err.message || 'ไม่สามารถโหลดข้อมูลชำระเงินได้');
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [accessToken, shopId]);

  // Refetch when page becomes visible again (e.g. return from Beam credit card tab)
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && accessToken && shopId) {
        fetchSummary();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [accessToken, shopId, fetchSummary]);

  // Clear ?after=group1 after we've used it; scroll to group2 when returning from CC
  useEffect(() => {
    if (afterGroup1 && hasGroup2) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('after');
        return next;
      }, { replace: true });
      group2SectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [afterGroup1, hasGroup2, setSearchParams]);

  // Clear ?after=group2; scroll to group1 when returning after paying group2 first
  useEffect(() => {
    if (afterGroup2 && hasGroup1) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('after');
        return next;
      }, { replace: true });
      group1SectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [afterGroup2, hasGroup1, setSearchParams]);

  // When both groups are paid (summary returns 0,0), go to select-action instead of showing "no payment required"
  useEffect(() => {
    if (summaryLoading || summary == null) return;
    const g1 = summary.group1Total ?? 0;
    const g2 = summary.group2Total ?? 0;
    if (g1 <= 0 && g2 <= 0) {
      onPaymentComplete();
    }
  }, [summaryLoading, summary, onPaymentComplete]);

  // Poll for group1 QR payment confirmation
  useEffect(() => {
    if (!g1QrImage || !accessToken || !shopId || g1Paid) return;
    const check = async () => {
      try {
        const data = await LiffService.getPaymentSummary(accessToken, shopId);
        if (process.env.NODE_ENV === 'development') {
          console.log('[LIFF Payment Poll]', { group1Total: data?.group1Total, group2Total: data?.group2Total });
        }
        if (data.group1Total <= 0) {
          if (pollRef.current) clearInterval(pollRef.current);
          setG1Paid(true);
          setSummary(data);
          if (data.bankInfo) setG2BankInfo(data.bankInfo);
          if (data.group2Total <= 0) onPaymentComplete();
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.log('[LIFF Payment Poll] error:', err?.message);
      }
    };
    pollRef.current = setInterval(check, POLL_INTERVAL_MS);
    check();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [g1QrImage, accessToken, shopId, g1Paid, onPaymentComplete]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // --- Group1 handlers ---
  const handleGroup1Pay = async () => {
    if (!g1Method) { setG1Error('กรุณาเลือกวิธีชำระเงิน'); return; }
    setG1Loading(true);
    setG1Error('');
    setG1QrImage(null);
    try {
      if (g1Method === 'creditCard') {
        const result = await LiffService.createPaymentLink(accessToken, shopId);
        if (result.paymentUrl) {
          try {
            const nav = JSON.parse(sessionStorage.getItem('liff_nav_data') || '{}');
            nav.selectActionItems = items || [];
            nav.ccPaymentGroup = 'group1';
            sessionStorage.setItem('liff_nav_data', JSON.stringify(nav));
          } catch (_e) {}
          window.location.href = result.paymentUrl;
          return;
        }
        setG1Error('ไม่สามารถสร้างลิงก์ชำระเงินได้');
      } else {
        const result = await LiffService.createQrCharge(accessToken, shopId);
        if (result.encodedImage) {
          setG1QrImage(result.encodedImage);
        } else {
          setG1Error(result.message || 'ไม่สามารถสร้าง QR ชำระเงินได้');
        }
      }
    } catch (err) {
      setG1Error(err.response?.data?.message || err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setG1Loading(false);
    }
  };

  // --- Group2 handlers ---
  const handleGroup2Pay = async () => {
    if (!g2Method) { setG2Error('กรุณาเลือกวิธีชำระเงิน'); return; }
    setG2Loading(true);
    setG2Error('');
    try {
      if (g2Method === 'creditCard') {
        const result = await LiffService.createGroup2PaymentLink(accessToken, shopId);
        if (result.paymentUrl) {
          try {
            const nav = JSON.parse(sessionStorage.getItem('liff_nav_data') || '{}');
            nav.selectActionItems = items || [];
            nav.ccPaymentGroup = 'group2';
            sessionStorage.setItem('liff_nav_data', JSON.stringify(nav));
          } catch (_e) {}
          window.location.href = result.paymentUrl;
          return;
        }
        setG2Error('ไม่สามารถสร้างลิงก์ชำระเงินได้');
      } else if (g2Method === 'bankTransfer') {
        if (!g2BankInfo && summary?.bankInfo) setG2BankInfo(summary.bankInfo);
        if (!g2BankInfo && !summary?.bankInfo) {
          const info = await LiffService.getGroup2BankInfo(accessToken, shopId);
          setG2BankInfo(info.bankInfo);
        }
        setG2ShowBankInfo(true);
      }
    } catch (err) {
      setG2Error(err.response?.data?.message || err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่');
    } finally {
      setG2Loading(false);
    }
  };

  const handleBankTransferDone = async () => {
    if (!accessToken || !shopId) return;
    try {
      const data = await LiffService.getPaymentSummary(accessToken, shopId);
      setSummary(data);
      if (data.bankInfo) setG2BankInfo(data.bankInfo);
      setG2ShowBankInfo(false);
      setG2Paid(true);
      const stillGroup1 = (data.group1Total ?? 0) > 0;
      const stillGroup2 = (data.group2Total ?? 0) > 0;
      if (!stillGroup1 && !stillGroup2) {
        onPaymentComplete();
      }
    } catch (_e) {
      setG2ShowBankInfo(false);
      onPaymentComplete();
    }
  };

  // --- Rendering ---
  if (summaryLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">กำลังโหลดข้อมูลชำระเงิน...</p>
        </div>
      </div>
    );
  }

  if (summaryError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-6 max-w-sm w-full text-center">
          <p className="text-red-600 text-sm mb-4">{summaryError}</p>
          <button onClick={onBack} className="text-green-600 underline text-sm">← กลับ</button>
        </div>
      </div>
    );
  }

  const showGroup1Section = hasGroup1 || showGroup1Done;
  const showGroup2Section = hasGroup2 || showGroup2Done;

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-8">
      <div className="max-w-sm mx-auto pt-4 space-y-4">
        {/* Step indicator */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
          <span className="font-medium text-amber-600">ขั้นตอนที่ 1/2: ชำระเงิน</span>
        </div>
        <p className="text-xs text-gray-500 mb-2">
          {singlePaymentOnly
            ? 'ชำระเงินแล้ว ระบบจะพาไปหน้าเลือกวิธีรับสินค้าอัตโนมัติ'
            : hasBothGroups
              ? 'หลังจากชำระครบทั้ง 2 ยอด ระบบจะพาไปหน้าเลือกวิธีรับสินค้าอัตโนมัติ'
              : 'ชำระยอดด้านล่างให้ครบ แล้วระบบจะพาไปหน้าเลือกวิธีรับสินค้า'}
        </p>

        {/* Hint when profile is company but this round has only one payment (no group2 amount yet) */}
        {summary?.isCompanyCustomer === true && !hasBothGroups && !singlePaymentOnly && (
          <p className="text-xs text-gray-500 mb-2">ลูกค้าบริษัท — รอบนี้มียอดชำระเพียงยอดเดียว</p>
        )}
        {summary?.isCompanyCustomer === false && singlePaymentOnly && (
          <p className="text-xs text-gray-500 mb-2">ลูกค้ารายย่อย — ชำระครั้งเดียว</p>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-gray-800">ชำระเงินก่อนเลือกวิธีรับสินค้า</h2>
          {typeof onBack === 'function' && !g1QrImage && !g2ShowBankInfo && (
            <button type="button" onClick={onBack} className="text-sm text-green-600 font-medium underline hover:text-green-700">
              ← กลับ
            </button>
          )}
        </div>

        {/* Overview — one or two amounts */}
        {singlePaymentOnly && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-medium text-blue-800 mb-2">ยอดที่ต้องชำระ</p>
            <div className="text-sm text-blue-700">
              <div className="flex justify-between font-semibold text-blue-900">
                <span>รวมทั้งหมด</span>
                <span>{fmt(summary?.group2Total || 0)} บาท</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">โอนเงินผ่านธนาคารหรือบัตรเครดิต</p>
            </div>
          </div>
        )}
        {(hasBothGroups || showGroup1Done || showGroup2Done) && !singlePaymentOnly && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm font-medium text-blue-800 mb-2">คุณมี 2 ยอดที่ต้องชำระ</p>
            <div className="space-y-1 text-sm text-blue-700">
              <div className="flex justify-between">
                <span>ยอดที่ 1: ค่าสินค้า</span>
                <span className="font-semibold">
                  {hasGroup1 ? `${fmt(summary.group1Total)} บาท` : 'ชำระแล้ว'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>ยอดที่ 2: ค่าน้ำหนัก/ค่าส่งไทย</span>
                <span className="font-semibold">
                  {hasGroup2 ? `${fmt(summary.group2Total)} บาท` : 'ชำระแล้ว'}
                </span>
              </div>
              <div className="border-t border-blue-200 pt-1 flex justify-between font-semibold text-blue-900">
                <span>รวมทั้งหมด</span>
                <span>{fmt((summary?.group1Total || 0) + (summary?.group2Total || 0))} บาท</span>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 1: Group1 — same page, continuous */}
        {showGroup1Section && (
          <div ref={group1SectionRef} className="bg-white rounded-2xl shadow-lg overflow-hidden p-5 space-y-4">
            {hasBothGroups && (
              <span className="inline-block px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-semibold">
                ขั้นตอนที่ 1 — ยอดที่ 1
              </span>
            )}
            {showGroup1Done ? (
              <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-700">ยอดที่ 1 ชำระเรียบร้อยแล้ว</span>
              </div>
            ) : !g1QrImage ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-800">
                    {hasGroup2 ? 'ยอดที่ 1: ค่าสินค้า + ค่าธรรมเนียม' : 'ยอดชำระ'}
                  </h3>
                  <span className="text-lg font-bold text-amber-600">{fmt(summary.group1Total)} บาท</span>
                </div>
                <p className="text-xs text-gray-500">ค่าสินค้า, ค่าส่งญี่ปุ่น (มัดจำ), ค่าธรรมเนียมการชำระเงิน</p>
                <div className="border-t border-gray-100 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">เลือกวิธีชำระเงิน</label>
                  <select
                    value={g1Method}
                    onChange={(e) => setG1Method(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-xl text-base text-gray-800 bg-white"
                  >
                    {!g1Method && <option value="">-- เลือกวิธีชำระเงิน --</option>}
                    <option value="qrCode">QR PromptPay</option>
                    <option value="creditCard">บัตรเครดิต (3.5%)</option>
                  </select>
                </div>
                {g1Error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{g1Error}</p>}
                <div className="border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={handleGroup1Pay}
                    disabled={g1Loading || !g1Method}
                    className="w-full py-3 bg-amber-400 hover:bg-amber-500 disabled:bg-gray-300 disabled:cursor-not-allowed text-gray-900 font-semibold rounded-xl shadow-sm text-base"
                  >
                    {g1Loading ? 'กำลังสร้างลิงก์...' : 'ชำระเงิน'}
                    {g1Method === 'creditCard' ? ' (ไปหน้าชำระของ Beam)' : g1Method === 'qrCode' ? ' (แสดง QR)' : ''}
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">สแกน QR Code เพื่อชำระเงิน (ยอดที่ 1)</p>
                  <span className="text-sm font-bold text-amber-600">{fmt(summary.group1Total)} บาท</span>
                </div>
                <div className="rounded-xl overflow-hidden border border-gray-200 bg-white p-6 flex flex-col items-center gap-3">
                  <img src={g1QrImage} alt="QR Code สำหรับชำระเงิน" className="max-w-[240px] w-full h-auto" />
                  <button
                    type="button"
                    onClick={() => downloadDataUrl(g1QrImage, 'qr-promptpay-charge.png')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 text-sm font-medium"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    บันทึกรูป QR
                  </button>
                </div>
                <p className="text-xs text-gray-500 text-center">
                  {hasGroup2 ? 'เมื่อชำระเงินเสร็จ ระบบจะอัปเดตด้านล่าง' : 'เมื่อชำระเงินเสร็จ คุณสามารถดำเนินการต่อได้'}
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                  <div className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                  กำลังตรวจสอบการชำระเงิน...
                </div>
              </div>
            )}
          </div>
        )}

        {/* SECTION 2: Group2 — same page, continuous */}
        {showGroup2Section && (
          <div ref={group2SectionRef} className="bg-white rounded-2xl shadow-lg overflow-hidden p-5 space-y-4">
            {hasBothGroups && (
              <span className="inline-block px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-semibold">
                ขั้นตอนที่ 2 — ยอดที่ 2
              </span>
            )}
            {showGroup2Done ? (
              <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2">
                <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-green-700">ยอดที่ 2 ชำระเรียบร้อยแล้ว</span>
              </div>
            ) : !g2ShowBankInfo ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-800">
                    {showGroup1Section ? 'ยอดที่ 2: ค่าน้ำหนัก / ค่าส่งไทย' : 'ยอดชำระ'}
                  </h3>
                  <span className="text-lg font-bold text-amber-600">{fmt(summary.group2Total)} บาท</span>
                </div>
                <p className="text-xs text-gray-500">
                  {singlePaymentOnly ? 'ยอดรวม (ค่าสินค้า, ค่าน้ำหนัก, ค่าส่ง ฯลฯ) — โอนธนาคารหรือบัตรเครดิต' : 'ค่าน้ำหนัก, ค่าส่งในประเทศไทย'}
                </p>
                <div className="border-t border-gray-100 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">เลือกวิธีชำระเงิน</label>
                  <select
                    value={g2Method}
                    onChange={(e) => setG2Method(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-xl text-base text-gray-800 bg-white"
                  >
                    {!g2Method && <option value="">-- เลือกวิธีชำระเงิน --</option>}
                    <option value="bankTransfer">โอนเงินผ่านธนาคาร</option>
                    <option value="creditCard">บัตรเครดิต (3.5%)</option>
                  </select>
                </div>
                {g2Error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{g2Error}</p>}
                <div className="border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={handleGroup2Pay}
                    disabled={g2Loading || !g2Method}
                    className="w-full py-3 bg-amber-400 hover:bg-amber-500 disabled:bg-gray-300 disabled:cursor-not-allowed text-gray-900 font-semibold rounded-xl shadow-sm text-base"
                  >
                    {g2Loading ? 'กำลังดำเนินการ...' : g2Method === 'creditCard' ? 'ชำระเงิน (ไปหน้าชำระของ Beam)' : g2Method === 'bankTransfer' ? 'ดูข้อมูลบัญชีธนาคาร' : 'ชำระเงิน'}
                  </button>
                </div>
              </>
            ) : (
              <BankTransferWithReceipt
                bankInfo={g2BankInfo || summary?.bankInfo}
                amount={summary.group2Total}
                accessToken={accessToken}
                shopId={shopId}
                onDone={handleBankTransferDone}
                onBack={() => setG2ShowBankInfo(false)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TOLERANCE_BAHT = 10;

/** Map API/backend receipt errors to user-friendly Thai message; still show manual-check option. */
function normalizeReceiptError(raw) {
  if (!raw || typeof raw !== 'string') return 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
  const s = raw.trim().toLowerCase();
  if (s.includes('found qrcode') && s.includes('not valid')) {
    return 'พบ QR ในสลิปแต่รูปแบบไม่ถูกต้องหรือสลิปไม่รองรับ — สามารถส่งให้แอดมินตรวจสอบได้ด้านล่าง';
  }
  if (s.includes('request failed with status code 400') || s === 'request failed with status code 400') {
    return 'ระบบตรวจสอบสลิปไม่ผ่าน — หากภาพสลิปถูกต้อง สามารถส่งให้แอดมินตรวจสอบได้ด้านล่าง';
  }
  return raw;
}

/**
 * Bank transfer with receipt upload and verification.
 */
const BankTransferWithReceipt = ({ bankInfo, amount, accessToken, shopId, onDone, onBack }) => {
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  const copyAccountNumber = async () => {
    try {
      await navigator.clipboard.writeText(bankInfo?.accountNumber || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* */ }
  };

  const handleFileChange = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setError('');
    setReceiptFile(file);
  };

  const submitReceiptWithManualCheck = async () => {
    if (!receiptFile || !accessToken || !shopId) return;
    setUploading(true);
    setError('');
    try {
      await LiffService.submitGroup2Receipt(accessToken, shopId, '', receiptFile, { manualCheck: true });
      onDone();
    } catch (err) {
      const raw = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
      setError(normalizeReceiptError(raw));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitReceipt = async () => {
    if (!receiptFile || !accessToken || !shopId) return;
    setUploading(true);
    setError('');
    try {
      const qrString = await readQrData(receiptFile);
      const verifyResult = await LiffService.verifyReceipt(accessToken, shopId, qrString);
      const receiptAmount = parseFloat(verifyResult?.data?.amount);
      if (!Number.isFinite(receiptAmount)) {
        setError('ไม่สามารถอ่านยอดเงินจากสลิปได้');
        setUploading(false);
        return;
      }
      if (Math.abs(receiptAmount - amount) > TOLERANCE_BAHT) {
        setError(`ยอดในสลิป (${receiptAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท) ไม่ตรงกับยอดที่ต้องชำระ (${amount.toLocaleString('th-TH', { minimumFractionDigits: 2 })} บาท)`);
        setUploading(false);
        return;
      }
      await LiffService.submitGroup2Receipt(accessToken, shopId, qrString, receiptFile);
      onDone();
    } catch (err) {
      const raw = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
      setError(normalizeReceiptError(raw));
    } finally {
      setUploading(false);
    }
  };

  if (!bankInfo) {
    return (
      <div className="space-y-4 text-center">
        <p className="text-sm text-gray-500">ไม่พบข้อมูลธนาคาร กรุณาติดต่อแอดมิน</p>
        <button onClick={onBack} className="w-full py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl">
          ← กลับ
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">← กลับ</button>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
        <h3 className="text-sm font-semibold text-gray-800">โอนเงินผ่านธนาคาร</h3>
      </div>

      <div className="bg-gray-50 rounded-xl p-4 space-y-3">
        <div>
          <p className="text-xs text-gray-500">ธนาคาร</p>
          <p className="text-sm font-semibold text-gray-800">{bankInfo.bankName || bankInfo.bankNameTh || '-'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">เลขบัญชี</p>
          <div className="flex items-center gap-2">
            <p className="text-base font-bold text-gray-900 tracking-wider">{bankInfo.accountNumber || '-'}</p>
            <button onClick={copyAccountNumber} className="text-xs text-blue-500 underline hover:text-blue-600">
              {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
            </button>
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500">ชื่อบัญชี</p>
          <p className="text-sm font-semibold text-gray-800">{bankInfo.accountNameTh || bankInfo.accountName || '-'}</p>
        </div>
        <div className="border-t border-gray-200 pt-3">
          <p className="text-xs text-gray-500">ยอดที่ต้องโอน</p>
          <p className="text-xl font-bold text-amber-600">{fmt(amount)} บาท</p>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">อัปโหลดสลิปโอนเงิน</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200"
        />
        {receiptFile && (
          <p className="text-xs text-gray-500 mt-1">{receiptFile.name}</p>
        )}
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      {receiptFile && (
        <>
          <button
            type="button"
            onClick={handleSubmitReceipt}
            disabled={uploading || !receiptFile}
            className="w-full py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-sm text-base"
          >
            {uploading ? 'กำลังตรวจสอบ...' : 'ยืนยันการชำระเงิน'}
          </button>
          {error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
              <p className="text-xs text-amber-800">
                หากคุณยืนยันว่าภาพสลิปถูกต้อง สามารถส่งให้แอดมินตรวจสอบภายหลังได้
              </p>
              <button
                type="button"
                onClick={submitReceiptWithManualCheck}
                disabled={uploading}
                className="w-full py-2.5 bg-amber-400 hover:bg-amber-500 disabled:bg-gray-300 disabled:cursor-not-allowed text-gray-900 font-medium rounded-xl text-sm"
              >
                {uploading ? 'กำลังส่ง...' : 'ยืนยันว่าภาพถูกต้อง (แอดมินจะตรวจสอบภายหลัง)'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LiffPaymentPage;
